import { apiClient } from './client';

/**
 * 회원 유형 코드 — INFO_USER.CD_USER_TYPE / UserTypeEnum 값과 동일해야 한다.
 * (백엔드가 UserTypeEnum.valueOf() 로 파싱하므로 축약 코드를 보내면 필터가 조용히 무시된다)
 */
export type MemberUserTypeCode =
  | 'USER_TYPE_PET_OWNER'
  | 'USER_TYPE_OWNER'
  | 'USER_TYPE_TEACHER';

/** SNS 유형 코드 — INFO_USER.CD_SNS_TYPE / SnsTypeEnum 값과 동일 */
export type MemberSnsTypeCode =
  | 'SNS_TYPE_KAKAO'
  | 'SNS_TYPE_NAVER'
  | 'SNS_TYPE_GOOGLE'
  | 'SNS_TYPE_APPLE';

/**
 * 회원 목록 조회 파라미터 (백엔드 API에 맞춤)
 */
export interface GetMembersParams {
  page?: number;
  size?: number;
  keyword?: string;
  userType?: MemberUserTypeCode;
  snsType?: MemberSnsTypeCode;
  /** 테스트 회원만 (Y) */
  testYn?: string;
}

/**
 * 회원 목록 응답 (백엔드 API에 맞춤)
 */
export interface GetMembersResponse {
  content: CustomerListResponseDto[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
  numberOfElements: number;
}

/**
 * 회원 상세 응답 (백엔드 API에 맞춤)
 */
export interface CustomerDetailResponseDto {
  userSeq: number;
  userType: MemberUserTypeCode;
  email: string;
  snsId: string;
  snsType: MemberSnsTypeCode;
  userName: string;
  nickname: string;
  phoneNumber: string;
  alarmNoticeYn: string;
  alarmMarketingYn: string;
  alarmSmsYn: string;
  alarmEmailYn: string;
  customerKey: string;
  logTermsSeq: number;
  delYn: string;
  testYn?: string;
  regDt: string;
  updDt: string;
}

/**
 * 회원 목록 응답 (백엔드 API에 맞춤)
 */
export interface CustomerListResponseDto {
  userSeq: number;
  userType: MemberUserTypeCode;
  email: string;
  snsType: MemberSnsTypeCode;
  userName: string;
  nickname: string;
  phoneNumber: string;
  alarmNoticeYn: string;
  alarmMarketingYn: string;
  alarmSmsYn: string;
  alarmEmailYn: string;
  petCount: number;
  usedBusinessCount: number;
  usedRegion: string;
  marketingConsent: string;
  delYn: string;
  testYn?: string;
  regDt: string;
  updDt: string;
}

/** 보호자 그룹 내 구분 */
export type GuardianType = 'MAIN' | 'SUB';

/** 보호자 연동 상태 */
export type GuardianMappingStatus = 'REQUESTED' | 'AGREED' | 'DENIED';

/**
 * 보호자 연동(가족 그룹) 구성원
 */
export interface GuardianGroupMemberDto {
  guardianGroupMappingSeq: number;
  userSeq: number;
  userName: string | null;
  /** 그룹 내 별칭 (초대 시 지정) */
  nickname: string | null;
  email: string | null;
  phoneNumber: string | null;
  cdGuardianType: GuardianType | null;
  mappingStatus: GuardianMappingStatus | null;
  petCount: number | null;
  /** 조회 대상 회원 본인 여부 */
  self: boolean;
  linkedAt: string | null;
}

/**
 * 보호자 연동 정보 응답
 */
export interface AdminGuardianGroupResponseDto {
  /** 연동 그룹 존재 여부 — false 면 members 는 빈 배열 */
  linked: boolean;
  guardianGroupSeq: number | null;
  targetGuardianType: GuardianType | null;
  targetMappingStatus: GuardianMappingStatus | null;
  members: GuardianGroupMemberDto[];
}

/**
 * 관리자용 반려동물 정보 응답 (백엔드 API에 맞춤)
 */
export interface AdminPetInfoResponseDto {
  petSeq: number;
  userSeq: number;
  petName: string;
  petRegNumber: string;
  cdPetBreed: number;
  cdPetGender: string;
  petBirthday: string;
  fileSeq: number;
  petWeight: number;
  petGradeSeq: number;
  mainPetYn: string;
  neuterYn: string;
  inoculation: string;
  personality: string;
  allergy: string;
  memo: string;
  fileUrl: string;
  delYn: string;
  regDt: string;
  updDt: string;
}

/**
 * 회원 관련 API 서비스 (백엔드 API에 맞춤)
 */
export const membersApi = {
  /**
   * 회원 목록 조회 (관리자용)
   */
  getMembers: async (
    params: GetMembersParams = {}
  ): Promise<GetMembersResponse> => {
    const response = await apiClient.get<GetMembersResponse>(
      '/admin/customers',
      { params }
    );
    return response.data;
  },

  /**
   * 회원 상세 조회 (관리자용)
   */
  getMember: async (userSeq: number): Promise<CustomerDetailResponseDto> => {
    const response = await apiClient.get<CustomerDetailResponseDto>(
      `/admin/customers/${userSeq}`
    );
    return response.data;
  },

  /**
   * 회원 상태 변경 (관리자용)
   */
  updateMemberStatus: async (
    userSeq: number,
    delYn: 'Y' | 'N'
  ): Promise<void> => {
    await apiClient.put(`/admin/customers/${userSeq}/status`, null, {
      params: { delYn },
    });
  },

  /**
   * 테스트 회원 지정/해제 (관리자용) — 테스트 회원은 테스트 매장까지 노출
   */
  updateMemberTestFlag: async (
    userSeq: number,
    testYn: 'Y' | 'N'
  ): Promise<void> => {
    await apiClient.put(`/admin/customers/${userSeq}/test-flag`, null, {
      params: { testYn },
    });
  },

  /**
   * 회원 검색 (기존 호환성)
   */
  searchMembers: async (query: string): Promise<CustomerListResponseDto[]> => {
    const response = await apiClient.get<GetMembersResponse>(
      '/admin/customers',
      {
        params: { keyword: query },
      }
    );
    return response.data.content;
  },

  /**
   * 회원 반려동물 목록 조회 (관리자용)
   */
  getMemberPets: async (
    userSeq: number
  ): Promise<AdminPetInfoResponseDto[]> => {
    const response = await apiClient.get<AdminPetInfoResponseDto[]>(
      `/admin/customers/${userSeq}/pets`
    );
    return response.data;
  },

  /**
   * 회원 보호자 연동(가족 그룹) 정보 조회 (관리자용)
   */
  getMemberGuardians: async (
    userSeq: number
  ): Promise<AdminGuardianGroupResponseDto> => {
    const response = await apiClient.get<AdminGuardianGroupResponseDto>(
      `/admin/customers/${userSeq}/guardians`
    );
    return response.data;
  },
};

// 사업자 관련 타입 정의
export interface BusinessListResponseDto {
  bizUserSeq: number;
  companySeq: number;
  bizUserName: string;
  companyName: string;
  email: string;
  phoneNumber: string;
  companyAddr: string;
  companyAddrDetail: string;
  businessNumber: string;
  mainTelNumber: string;
  createdAt: string;
  status: string;
  activationRequestStatus?: ActivationRequestStatus;
  businessFileSeq: number;
  companyFileSeq: number;
  cdUserType?:
    | 'USER_TYPE_OWNER'
    | 'USER_TYPE_TEACHER'
    | 'USER_TYPE_PET_SITTER'
    | 'USER_TYPE_PET_OWNER';
}

export type ActivationRequestStatus =
  | 'NOT_REQUESTED'
  | 'REQUESTED'
  | 'AGREED'
  | 'DENIED';

export interface BusinessDetailResponseDto {
  bizUserSeq: number;
  companySeq: number;
  bizUserName: string;
  companyName: string;
  email: string;
  phoneNumber: string;
  zipcode: string;
  companyAddr: string;
  companyAddrDetail: string;
  region: string;
  latitude: number;
  longitude: number;
  businessNumber: string;
  mainTelNumber: string;
  mainBank: string;
  mainAccountNumber: string;
  depositor: string;
  playgroundYn: string;
  alarmNoticeYn: string;
  alarmMarketingYn: string;
  alarmSmsYn: string;
  alarmEmailYn: string;
  createdAt: string;
  status: string;
  activationRequestStatus?: ActivationRequestStatus;
  businessFileSeq: number;
  companyFileSeq: number;
  cdUserType?:
    | 'USER_TYPE_OWNER'
    | 'USER_TYPE_TEACHER'
    | 'USER_TYPE_PET_SITTER'
    | 'USER_TYPE_PET_OWNER';
  businessFileUrl?: string;
  companyFileUrl?: string;
}

export interface UpdateBusinessCompanyActiveRequest {
  companySeq: number;
  activeYn: 'Y' | 'N';
  deniedReason?: string;
}

/**
 * 활성화 요청과 무관하게 매장을 직접 켜고 끌 때 사용 (승인/반려와는 다른 API)
 */
export interface UpdateCompanyActivationStateRequest {
  companySeq: number;
  activeYn: 'Y' | 'N';
  /** 활성화 시 사업주에게 알림장/메일 발송 여부. 비활성화는 발송하지 않는다 */
  notifyYn?: 'Y' | 'N';
}

/**
 * 사업자 목록 조회 파라미터
 */
export interface GetBusinessesParams {
  page?: number;
  size?: number;
  search?: string;
  status?: string;
}

/**
 * 사업자 목록 응답 (직접 Page 구조)
 */
export interface GetBusinessesResponse {
  content: BusinessListResponseDto[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
  empty: boolean;
  pageable: any;
  numberOfElements: number;
  sort: any;
}

/**
 * 사업자 관리 API
 */
export const businessApi = {
  /**
   * 사업자 목록 조회
   */
  getBusinesses: async (
    params: GetBusinessesParams = {}
  ): Promise<GetBusinessesResponse> => {
    const response = await apiClient.get<GetBusinessesResponse>(
      '/admin/businesses',
      { params }
    );
    return response.data;
  },

  /**
   * 사업자 상세 조회
   */
  getBusinessDetail: async (
    bizUserSeq: number
  ): Promise<BusinessDetailResponseDto> => {
    const response = await apiClient.get<BusinessDetailResponseDto>(
      `/admin/businesses/${bizUserSeq}`
    );
    return response.data;
  },

  /**
   * 사업자 상태 변경
   */
  updateBusinessStatus: async (
    bizUserSeq: number,
    status: string
  ): Promise<void> => {
    await apiClient.put(`/admin/businesses/${bizUserSeq}/status`, null, {
      params: { status },
    });
  },

  /**
   * 매장 활성화 요청 승인(Y) / 반려(N)
   */
  updateBusinessCompanyActive: async (
    payload: UpdateBusinessCompanyActiveRequest
  ): Promise<void> => {
    await apiClient.put('/admin/businesses/company/active', payload);
  },

  /**
   * 매장 활성화 상태 직접 변경 — 활성화 요청이 없어도 관리자가 켜고 끌 수 있다.
   * 반려가 아니므로 반려 사유가 남지 않는다.
   */
  updateCompanyActivationState: async (
    payload: UpdateCompanyActivationStateRequest
  ): Promise<void> => {
    await apiClient.put('/admin/businesses/company/activation-state', payload);
  },

  /**
   * 도장 사용 PIN 초기화
   * PIN을 삭제(미등록 상태로 초기화)하고 실패 횟수/잠금도 해제한다.
   * 사업자는 이후 새 PIN을 다시 등록해야 한다.
   */
  resetBizSealPin: async (bizUserSeq: number): Promise<void> => {
    await apiClient.post(`/admin/businesses/${bizUserSeq}/seal-pin/reset`);
  },
};

// 매장 관련 타입 정의
export interface CompanyListResponseDto {
  companySeq: number;
  companyName: string;
  businessNumber: string;
  mainTelNumber: string;
  region: string;
  companyAddr: string;
  companyAddrDetail: string;
  ceoName: string | null;
  ownerCount: number;
  teacherCount: number;
  activeYn: string;
  testYn?: string;
  activationRequestStatus?: ActivationRequestStatus;
  createdAt: string;
}

export interface CompanyMemberDto {
  bizUserSeq: number;
  bizUserName: string;
  cdUserType:
    | 'USER_TYPE_OWNER'
    | 'USER_TYPE_TEACHER'
    | 'USER_TYPE_PET_SITTER'
    | 'USER_TYPE_PET_OWNER';
  email: string;
  phoneNumber: string;
  createdAt: string;
  status: string;
}

export interface CompanyProvideDto {
  cdProvideType: string | null;
  provideName: string;
  activeYn: string;
}

export interface CompanyFacilityDto {
  cdFacilityType: string | null;
  facilityName: string;
  activeYn: string;
}

export interface CompanyBusinessTimeDto {
  dayOfWeek: string;
  startTime: string | null;
  endTime: string | null;
  closedYn: string;
}

export interface CompanyDetailResponseDto {
  companySeq: number;
  companyName: string;
  businessNumber: string;
  mainTelNumber: string;
  zipcode: string;
  companyAddr: string;
  companyAddrDetail: string;
  region: string;
  latitude: number;
  longitude: number;
  mainBank: string;
  mainAccountNumber: string;
  depositor: string;
  playgroundYn: string;
  maxWeight: number | null;
  duplicateReservYn: string;
  reservCapacityLimitYn?: string | null;
  dailyReservCapacity?: number | null;
  kakaoAlimUseYn: string | null;
  kakaoAlimFallbackSmsYn: string | null;
  remainTicketIssueQty: number | null;
  dtLastAccess: string | null;
  activeYn: string;
  testYn?: string;
  activationRequestStatus?: ActivationRequestStatus;
  deniedReason?: string;
  /** 크레딧 차감 면제 종료 일시 (null=면제 없음). 현재보다 미래면 면제 적용 중 */
  exemptUntil?: string | null;
  exemptReason?: string | null;
  createdAt: string;
  businessFileSeq: number | null;
  businessFileUrl?: string;
  companyFileSeq: number | null;
  companyFileUrl?: string;
  members: CompanyMemberDto[];
  provides: CompanyProvideDto[];
  facilities: CompanyFacilityDto[];
  businessTimes: CompanyBusinessTimeDto[];
  promoPhotos?: CompanyPromoPhotoDto[];
}

/** 매장 홍보 사진/영상 (대표 우선 정렬) */
export interface CompanyPromoPhotoDto {
  companyPhotoSeq: number;
  fileSeq: number;
  fileUrl: string | null;
  representativeYn: string | null;
  sortOrder: number | null;
  cdMediaType: string | null;
  thumbnailUrl: string | null;
}

/**
 * 매장이 등록한 이용권 상품 + 발급(판매) 집계
 */
export interface AdminCompanyTicketDto {
  productTicketSeq: number;
  name: string;
  contents: string | null;
  cdProductType: string;
  cdTicketType: string | null;
  cost: number | null;
  /** 할인율 (%) */
  discount: number | null;
  amount: number | null;
  splitYn: 'Y' | 'N' | null;
  /** 총 횟수 (호텔은 박수) */
  splitCount: number | null;
  minPurchaseQty: number | null;
  minUseQty: number | null;
  /** 구매일 기준 유효기간 일수 (null 이면 고정 기간) */
  durationDays: number | null;
  dtUsable: string | null;
  dtExpired: string | null;
  activeYn: 'Y' | 'N';
  dataOrder: number | null;
  dtRegist: string | null;
  /** 결제완료 기준 발급 건수 */
  issuedCount: number;
  /** 결제완료 기준 누적 결제액 */
  salesAmount: number;
  lastIssuedAt: string | null;
}

/**
 * 매장 목록 조회 파라미터
 */
export interface GetCompaniesParams {
  page?: number;
  size?: number;
  search?: string;
  status?: string;
}

/**
 * 매장 목록 응답 (직접 Page 구조)
 */
export interface GetCompaniesResponse {
  content: CompanyListResponseDto[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
  empty: boolean;
  pageable: any;
  numberOfElements: number;
  sort: any;
}

/**
 * 매장 관리 API
 */
export interface UpdateCompanyInfoPayload {
  // 기본정보(주소·연락처)
  mainTelNumber?: string;
  zipcode?: string;
  companyAddr?: string;
  companyAddrDetail?: string;
  region?: string;
  // 기본정보(운영 설정)
  playgroundYn?: string;
  maxWeight?: number | null;
  duplicateReservYn?: string;
  reservCapacityLimitYn?: string | null;
  dailyReservCapacity?: number | null;
  // 정산(계좌)
  mainBank?: string;
  mainAccountNumber?: string;
  depositor?: string;
  // 운영
  kakaoAlimUseYn?: string;
  kakaoAlimFallbackSmsYn?: string;
  remainTicketIssueQty?: number | null;
  // 테스트 매장 여부(Y/N)
  testYn?: string;
  // 미디어 보존일(null/0 = 전역 기본 사용)
  retentionDays?: number | null;
}

export interface CompanyStorageCategory {
  key: string;
  label: string;
  bytes: number;
  count: number;
  recentAt: string | null;
}

export interface CompanyStoragePhysical {
  bytes: number;
  objectCount: number;
  thumbnailBytes: number;
  thumbnailCount: number;
  computed: boolean;
  tooMany: boolean;
  estimated: boolean;
  sampleSize: number;
}

export interface CompanyStorageDto {
  totalBytes: number;
  totalCount: number;
  categories: CompanyStorageCategory[];
  physical: CompanyStoragePhysical | null;
  /** 매장별 보존일(null=전역 기본 사용) */
  retentionDays: number | null;
  /** 전역 기본 보존일 */
  defaultRetentionDays: number;
  /** 실제 적용 보존일 = 매장 설정 ?? 전역 기본 */
  effectiveRetentionDays: number;
}

export interface CompanyGuardianPetUsage {
  petSeq: number;
  petName: string | null;
  reservationCount: number;
  lastReservAt: string | null;
}

export interface CompanyGuardianItem {
  userSeq: number;
  userName: string | null;
  phoneNumber: string | null;
  email: string | null;
  petNames: string | null;
  petCount: number;
  reservationCount: number;
  lastReservAt: string | null;
  pets: CompanyGuardianPetUsage[];
}

export interface CompanyGuardiansDto {
  totalCount: number;
  guardians: CompanyGuardianItem[];
}

export const companyApi = {
  /**
   * 매장 목록 조회
   */
  getCompanies: async (
    params: GetCompaniesParams = {}
  ): Promise<GetCompaniesResponse> => {
    const response = await apiClient.get<GetCompaniesResponse>(
      '/admin/companies',
      { params }
    );
    return response.data;
  },

  /**
   * 매장 정보 수정 (주소·연락처 등 한정)
   */
  updateCompanyInfo: async (
    companySeq: number,
    payload: UpdateCompanyInfoPayload
  ): Promise<void> => {
    await apiClient.put(`/admin/companies/${companySeq}/info`, payload);
  },

  /**
   * 매장 저장용량 조회 (physical=true 면 실 S3 물리 용량까지 온디맨드 계산)
   */
  getCompanyStorage: async (
    companySeq: number,
    physical = false
  ): Promise<CompanyStorageDto> => {
    const response = await apiClient.get<CompanyStorageDto>(
      `/admin/companies/${companySeq}/storage`,
      { params: { physical } }
    );
    return response.data;
  },

  /**
   * 매장 이용보호자 조회 (예약이력 또는 최근 1년 유효계약 보유 보호자)
   */
  getCompanyGuardians: async (
    companySeq: number
  ): Promise<CompanyGuardiansDto> => {
    const response = await apiClient.get<CompanyGuardiansDto>(
      `/admin/companies/${companySeq}/guardians`
    );
    return response.data;
  },

  /**
   * 매장 상세 조회 (매장 정보 + 소속 사업자/선생님)
   */
  getCompanyDetail: async (
    companySeq: number
  ): Promise<CompanyDetailResponseDto> => {
    const response = await apiClient.get<CompanyDetailResponseDto>(
      `/admin/companies/${companySeq}`
    );
    return response.data;
  },

  /**
   * 매장이 등록한 이용권 상품 목록 조회 (상품별 발급/판매 집계 포함)
   */
  getCompanyTickets: async (
    companySeq: number
  ): Promise<AdminCompanyTicketDto[]> => {
    const response = await apiClient.get<AdminCompanyTicketDto[]>(
      `/admin/companies/${companySeq}/tickets`
    );
    return response.data;
  },

  /** 매장 홍보사진 삭제 */
  deleteCompanyPhoto: async (
    companySeq: number,
    companyPhotoSeq: number
  ): Promise<void> => {
    await apiClient.delete(
      `/admin/companies/${companySeq}/photos/${companyPhotoSeq}`
    );
  },

  /** 매장 홍보사진 대표 지정 */
  setRepresentativeCompanyPhoto: async (
    companySeq: number,
    companyPhotoSeq: number
  ): Promise<void> => {
    await apiClient.put(
      `/admin/companies/${companySeq}/photos/${companyPhotoSeq}/representative`
    );
  },
};
